import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CrossIcon, FiltersIcon } from '../icons';

// A "Filtri" button that opens the page's filter dropdowns in a panel. The panel is an
// absolutely-positioned DOM descendant — NOT a portal — so the fields stay inside the
// enclosing <form> and the form's DOM-based value collection keeps working. Selecting a
// value auto-applies (the form's onChange); a "Reset all" clears every field + the L1
// search and re-runs the filter.
//
// Fields are rendered in a clean labelled grid: each field's own (truncation-prone)
// floating label is stripped and shown as a clear uppercase caption above a full-width
// control. Inline styles only (shared lib; consuming app Tailwind does not scan it).
const ACCENT = '#E6616A';
const FilterField = ({
  field
}) => {
  const label = field.props?.label;
  const control = /*#__PURE__*/React.cloneElement(field, {
    label: undefined,
    fullWidth: true,
    xs: undefined,
    md: undefined,
    lg: undefined,
    margin: 'none'
  });
  return /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      minWidth: 0
    }
  }, label && /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      color: '#73747A'
    }
  }, label), control);
};
const FilterPopover = ({
  count = 0,
  label,
  fields = [],
  onReset
}) => {
  const {
    t
  } = useTranslation();
  const [open, setOpen] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const ref = useRef(null);
  const active = count > 0;
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onDoc = e => {
      if (!ref.current || ref.current.contains(e.target)) {
        return;
      }
      // MUI Select/menu/date-picker render their options in a body portal — clicking an
      // option must NOT count as an outside click (otherwise picking a value closes us).
      if (e.target.closest && e.target.closest('.MuiPopover-root, .MuiMenu-root, .MuiDialog-root, .MuiModal-root, .MuiAutocomplete-popper')) {
        return;
      }
      setOpen(false);
    };
    const onEsc = e => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);
  const handleReset = () => {
    // Clear the L1 search term (provided by the caller).
    if (onReset) {
      onReset();
    }
    // Remount the fields so each control returns to its default value...
    setResetKey(k => k + 1);
    // ...then let the form re-collect + re-filter from the reset DOM values. LegacyForm's
    // onChange fires from a control's change event bubbling to the <form>, so dispatch from
    // a child control (not the form element itself).
    setTimeout(() => {
      const form = ref.current && ref.current.closest('form');
      if (!form || !ref.current) {
        return;
      }
      // Scope to this popover's own fields (not the whole form — e.g. the L1 search input
      // lives outside it) and dispatch on every one, not just the first: each freshly
      // remounted control already has React's internal _valueTracker recording the reset
      // value, so a plain dispatchEvent is silently swallowed (React thinks nothing
      // changed). Force the tracker's stale value first, exactly like SelectOption's own
      // triggerChangeForHiddenField does, so React's synthetic onChange actually fires.
      const controls = ref.current.querySelectorAll('input, select, textarea');
      controls.forEach(control => {
        const tracker = control._valueTracker;
        if (tracker) {
          tracker.setValue(`${control.value}-reset`);
        }
        control.dispatchEvent(new Event('input', {
          bubbles: true
        }));
        control.dispatchEvent(new Event('change', {
          bubbles: true
        }));
      });
    }, 0);
  };
  return /*#__PURE__*/React.createElement("div", {
    ref: ref,
    style: {
      position: 'relative'
    }
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: () => setOpen(o => !o),
    "aria-expanded": open,
    style: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      height: 36,
      padding: '0 12px',
      borderRadius: 8,
      border: `1px solid ${active ? ACCENT : '#d1d5db'}`,
      background: '#fff',
      color: active ? ACCENT : '#4b5563',
      fontSize: 13,
      fontWeight: 500,
      cursor: 'pointer',
      whiteSpace: 'nowrap'
    }
  }, /*#__PURE__*/React.createElement(FiltersIcon, {
    style: {
      fontSize: 18
    }
  }), label, active && /*#__PURE__*/React.createElement("span", {
    style: {
      marginLeft: 2,
      minWidth: 18,
      height: 18,
      padding: '0 4px',
      borderRadius: 999,
      background: ACCENT,
      color: '#fff',
      fontSize: 10,
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }
  }, count > 99 ? '99+' : count)), /*#__PURE__*/React.createElement("div", {
    style: {
      display: open ? 'block' : 'none',
      position: 'absolute',
      left: 0,
      top: 'calc(100% + 8px)',
      zIndex: 1000,
      width: 'min(94vw, 720px)',
      borderRadius: 12,
      border: '1px solid #e5e7eb',
      background: '#fff',
      padding: 16,
      boxShadow: '0 10px 30px rgba(0,0,0,0.12)'
    }
  }, /*#__PURE__*/React.createElement("div", {
    key: resetKey,
    style: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
      gap: 16,
      alignItems: 'end'
    }
  }, fields.map((field, i) => /*#__PURE__*/React.createElement(FilterField, {
    key: field.props?.name || i,
    field: field
  }))), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: 14
    }
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: handleReset,
    style: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      height: 34,
      padding: '0 12px',
      borderRadius: 8,
      border: '1px solid #e5e7eb',
      background: '#fff',
      color: '#73747A',
      fontSize: 13,
      fontWeight: 500,
      cursor: 'pointer'
    }
  }, /*#__PURE__*/React.createElement(CrossIcon, {
    style: {
      fontSize: 16
    }
  }), t('GENERAL.clearFilters', 'Reimposta filtri')))));
};
export default FilterPopover;